import React, { useState } from 'react'
import classnames from 'classnames'
import { Icon, Stack } from '@crossfit/ui'

import { AffiliateMemberCertification, Role } from 'types'
import style from './style.module.scss'
import { Text } from '@crossfit/design-system'
import TeamRolesModal from '../Dashboard/TeamRolesModal'
import ToolTip from 'pages/PartnerNetwork/AffiliateId/Tooltip'
import { higestUserCredential } from 'utilities/credentials'

type UserListProps = {
  children: React.ReactNode
  isMobile: boolean
  showRole?: boolean
  showExpiration?: boolean
}

export function UserList({
  children,
  isMobile,
  showRole = true,
  showExpiration = false,
}: UserListProps) {
  const [showRolesModal, setShowRoleModal] = useState(false)

  return isMobile ? (
    <Stack mb={10}>{children}</Stack>
  ) : (
    <table className='cf-table' style={{ marginBottom: '48px' }}>
      <thead className={style.tableHeader}>
        <tr>
          <th>Teammate</th>
          <th>Credentials</th>
          {showExpiration && (
            <th>
              <div className={style.expirationHeaderCell}>
                Expiration
                <ToolTip
                  trigger={isMobile ? 'click' : 'mouseenter focus click'}
                  placement={isMobile ? 'bottom' : 'top-end'}
                  content={
                    <p style={{ zIndex: 800 }}>
                      Date current trainer credential expires.
                    </p>
                  }
                >
                  <div className={style.tooltipContainer}>
                    <Icon name='info' className={style.icon} />
                  </div>
                </ToolTip>
              </div>
            </th>
          )}
          {showRole && (
            <th className={style.roleHeaderCell}>
              Role
              <Icon
                name='info'
                className={style.info}
                onClick={() => {
                  setShowRoleModal(true)
                }}
              />
              {showRolesModal && (
                <TeamRolesModal
                  onClose={() => {
                    setShowRoleModal(false)
                  }}
                />
              )}
            </th>
          )}
          <th>&nbsp;</th>
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  )
}

type UserListItemProps = {
  isMe?: boolean
  isMobile: boolean
  showRole?: boolean
  name: string
  roles?: Role[]
  email: string
  certifications: AffiliateMemberCertification[]
  menu: React.ReactNode
  showExpiration?: boolean
}

export function UserListItem({
  isMe,
  isMobile,
  showRole = true,
  name,
  roles,
  email,
  certifications,
  menu,
  showExpiration = false,
}: UserListItemProps) {
  const highestCred = higestUserCredential(certifications)
  const expirationIsValid =
    highestCred?.expiration && new Date() < new Date(highestCred?.expiration)
  const filteredRoles = roles
    ?.filter((r) => [Role.LOR, Role.Manager, Role.Coach].includes(r))
    ?.map((r) => (r === Role.LOR ? 'OWNER' : r))
  const [showRolesModal, setShowRolesModal] = useState(false)

  return isMobile ? (
    <div className={classnames(style.mobileTile, { [style.me]: isMe })}>
      <div className={style.mobileCol}>
        <div className={style.colLabel}>
          <Text type='body' size='md' bold>
            Person
          </Text>
        </div>
        <div className={style.colLabel}>
          <Text type='body' size='md' bold>
            Credentials
          </Text>
        </div>
        {showExpiration && (
          <div className={classnames(style.colLabel, style.roleCol)}>
            <Text type='body' size='md' bold>
              Expiration
            </Text>
            <ToolTip
              trigger={isMobile ? 'click' : 'mouseenter focus click'}
              placement={isMobile ? 'bottom' : 'top-end'}
              content={
                <p style={{ zIndex: 800 }}>
                  Date current trainer credential expires.
                </p>
              }
            >
              <div className={style.tooltipContainer}>
                <Icon name='info' className={style.icon} />
              </div>
            </ToolTip>
          </div>
        )}
        <div className={classnames(style.colLabel, style.roleCol)}>
          <Text type='body' size='md' bold>
            Roles
          </Text>
          <Icon
            name='info'
            className={style.info}
            onClick={() => {
              setShowRolesModal(true)
            }}
          />
          {showRolesModal && (
            <TeamRolesModal
              onClose={() => {
                setShowRolesModal(false)
              }}
            />
          )}
        </div>
      </div>
      <div className={style.mobileCol}>
        <div className={style.emailContainer}>
          <div className={style.colLabel}>
            <Text type='body' size='md' bold>
              {name}
            </Text>
            <Text type='body' size='md' className={style.email}>
              {email}
            </Text>
          </div>
          <button>{menu}</button>
        </div>
        <div className={style.colLabel}>
          <Text type='body' size='md'>
            {highestCred?.level}
          </Text>
        </div>
        <div className={style.colLabel}>
          <Text
            type='body'
            as='p'
            size='md'
            className={classnames(style.expiration, {
              [style.expirationValid]:
                expirationIsValid && highestCred?.expiration,
              [style.expirationInvalid]:
                !expirationIsValid && highestCred?.expiration,
            })}
          >
            {showExpiration && highestCred?.expiration
              ? highestCred?.expiration
              : null}
          </Text>
        </div>
        <div className={style.colLabel}>
          <Text
            type='body'
            as='p'
            size='md'
            className={classnames(style.expiration, {
              [style.expirationValid]:
                expirationIsValid && highestCred?.expiration,
              [style.expirationInvalid]:
                !expirationIsValid && highestCred?.expiration,
            })}
          >
            {showExpiration && highestCred?.expiration
              ? highestCred?.expiration
              : null}
          </Text>
        </div>
        <div className={style.colLabel}>
          <Text type='body' size='md'>
            {showRole && filteredRoles?.map((c) => c.toUpperCase()).join(',')}
          </Text>
        </div>
      </div>
    </div>
  ) : (
    <tr className={classnames(style.tableRow, { [style.me]: isMe })}>
      <td className={style.tableCell}>
        <Stack spacing={1} direction='vertical'>
          <div className={style.userName}>
            {name}
            {isMe && ` (me)`}
          </div>
          <div>{email}</div>
        </Stack>
      </td>
      <td className={style.tableCell}>
        <ul>
          <li key={highestCred?.level}>{highestCred?.level}</li>
        </ul>
      </td>
      {showExpiration && (
        <td className={style.tableCell}>
          <Text
            type='body'
            as='span'
            size='sm'
            bold
            className={classnames(style.expiration, {
              [style.expirationValid]:
                expirationIsValid && highestCred?.expiration,
              [style.expirationInvalid]:
                !expirationIsValid && highestCred?.expiration,
            })}
          >
            {highestCred?.expiration}
          </Text>
        </td>
      )}
      {showRole && (
        <td className={style.tableCell}>
          {filteredRoles?.map((c) => c.toUpperCase()).join(',')}
        </td>
      )}
      <td className={classnames(style.menuCell, style.tableCell)}>{menu}</td>
    </tr>
  )
}
